import { useSyncExternalStore } from 'react';
import {
  EMPTY_QUEUE,
  queueAdvance,
  queueAppend,
  queueAppendAndPlay,
  queueClear,
  queueFollowVideo,
  queueFromPlaylist,
  queueInsertNext,
  queueMove,
  queuePlay,
  queuePlayNext,
  queueRemove,
  queueRemoveVideo,
  queueReverse,
  queueStart,
  queueUnlink,
  readStoredQueue,
  toStoredQueue,
  type QueuePlaylistWindow,
  type QueueState,
  type QueueVideo,
} from '@visious/core';

/**
 * Where the queue lives: **`sessionStorage`**, which is per tab, survives a reload and dies with the
 * tab. That is the requirement spelled exactly — a queue is what *this* tab is watching through, so
 * opening a second tab starts with an empty one and closing the tab is the end of the list.
 *
 * `router/scroll.ts` already keeps the back stack's scroll positions in the same place for the same
 * reason, and the one behaviour worth stating because it is not obvious: leaving the site and coming
 * back **in the same tab** keeps the queue. The storage area belongs to the tab and the origin, not
 * to a page load, so a reader who follows a link out and presses Back finds their list still there.
 * That was chosen rather than inherited — the alternative is reading
 * `PerformanceNavigationTiming.type` at boot and dropping anything that is not a reload, which would
 * throw away a list on exactly the navigation a reader most expects to survive.
 *
 * There is no `storage` event to listen for: `sessionStorage` is not shared between tabs, so unlike
 * `playlists/orderMemory.ts` and the settings store this one has no second writer to reconcile with.
 * The in-memory snapshot is the whole subscription.
 *
 * R4's division of labour holds — `JSON.parse` happens here, and core decides whether what came back
 * is a queue (`readStoredQueue`, which drops a broken row rather than the list).
 */

const KEY = 'visious.queue';

/** Read once and kept in memory: this is on the render path and storage reads are synchronous. */
let snapshot: QueueState = load();
const listeners = new Set<() => void>();

/**
 * Whether the session the queue is about to hand the player should start playing on its own.
 *
 * **Moving the cursor is a request to play.** An advance, a press on a row, a removal that pushes the
 * queue on to the next video — each of those is somebody working a list, and a list that stopped dead
 * between every two videos would not be a queue. The Playback tab's "Start playing on arrival" is
 * about *opening a page*, so it does not get a vote on any of them.
 *
 * Creating a queue is the one cursor move that is an arrival, and there the setting decides — which
 * is why {@link startQueue} takes the answer rather than assuming one: the store cannot read settings
 * and should not try.
 *
 * Module state beside the queue rather than a field in it, because it describes a *transition* and
 * not a list: it must never be persisted, or reloading a tab would replay a press that happened
 * yesterday. A reload is an arrival.
 */
let playOnArrival = false;

function load(): QueueState {
  try {
    const raw = globalThis.sessionStorage?.getItem(KEY);
    if (raw === null || raw === undefined) return EMPTY_QUEUE;
    return readStoredQueue(JSON.parse(raw) as unknown);
  } catch {
    // A blocked storage context, or a blob some other version wrote. Both mean "start with no
    // queue", which costs the reader a list they can rebuild and nothing else.
    return EMPTY_QUEUE;
  }
}

function write(next: QueueState, play = playOnArrival): void {
  if (next === snapshot && play === playOnArrival) return;
  playOnArrival = play;
  snapshot = next;
  try {
    if (next.entries.length === 0) globalThis.sessionStorage?.removeItem(KEY);
    else globalThis.sessionStorage?.setItem(KEY, JSON.stringify(toStoredQueue(next)));
  } catch {
    // Swallowed, and the in-memory value kept: a queue that does not survive a reload is still a
    // queue for the rest of this page's life, which is most of what it is for.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = (): QueueState => snapshot;
const getPlayOnArrival = (): boolean => playOnArrival;
/** Stable, not rebuilt per call — a changing server snapshot re-renders forever. */
const getServerSnapshot = (): QueueState => EMPTY_QUEUE;
const FALSE = (): boolean => false;

/** The queue as it stands. Every screen that shows or changes one reads it through here. */
export function useQueue(): QueueState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Whether the queue's next session starts itself. Subscribed rather than read, because the answer
 * changes in the same write that changes which video is playing — see {@link playOnArrival}.
 */
export function useQueuePlayOnArrival(): boolean {
  return useSyncExternalStore(subscribe, getPlayOnArrival, FALSE);
}

/**
 * The queue outside React — for the one caller that cannot be a hook: an event handler that has to
 * decide what a press *means* from the state at the moment of the press (`queue/model.ts`'s
 * `decideCardPress`), before any of it reaches a component.
 */
export function readQueue(): QueueState {
  return snapshot;
}

/* ------------------------------------------------------------------------------------------------
 * The acts
 *
 * One thin wrapper per core edit. They exist so that no component ever holds a `QueueState` long
 * enough to write a stale one back: each reads the live snapshot itself, which is what makes two
 * presses in one frame compose instead of one overwriting the other.
 * ---------------------------------------------------------------------------------------------- */

/**
 * Start a queue over. `play` is the reader's "Start playing on arrival" — the caller's to supply,
 * since this is the one queue act that is an arrival rather than a press.
 */
export function startQueue(videos: readonly QueueVideo[], play: boolean): void {
  write(queueStart(snapshot, videos), play);
}

export function appendToQueue(videos: readonly QueueVideo[]): void {
  write(queueAppend(snapshot, videos));
}

/**
 * The videos another Visious tab handed over, onto the end of this list — `tabs/useTabImport.ts`.
 *
 * `queueAppendAndPlay` rather than a plain append, and the `play` flag follows the cursor rather
 * than being asserted: an import into a running list changes nothing about the playback, while one
 * into a list that had played itself out moves the queue on to the first video that arrived, and
 * moving the cursor is a request to play (see {@link playOnArrival}).
 */
export function importIntoQueue(videos: readonly QueueVideo[]): void {
  if (videos.length === 0) return;
  const next = queueAppendAndPlay(snapshot, videos);
  write(next, next.cursor !== snapshot.cursor);
}

export function queueNext(videos: readonly QueueVideo[]): void {
  write(queueInsertNext(snapshot, videos));
}

/**
 * Put one video next in line, moving it if the queue already holds it — a card's **Play next**.
 * What is playing does not change, so this is not a request to play (`core/queue/model.ts`).
 */
export function playNextInQueue(video: QueueVideo): void {
  write(queuePlayNext(snapshot, video));
}

export function removeFromQueue(key: string): void {
  // Removing the playing row moves the queue on to the next video (`core/queue/model.ts`), and that
  // successor plays for the same reason an advance's does.
  write(queueRemove(snapshot, key), true);
}

/**
 * Take every copy of a video out — the card button's Remove, which names a video and not a row
 * (`core/queue/model.ts` argues the "every copy" part).
 */
export function removeVideoFromQueue(videoId: string): void {
  write(queueRemoveVideo(snapshot, videoId), true);
}

/**
 * Turn the whole list around — the panel's Reverse, which on a queue really moves the rows.
 *
 * The default `play` rather than `true`: the cursor's *index* changes because everything around it
 * moved, but what is playing does not, so this is not one of the cursor moves that are a request to
 * play (see {@link playOnArrival}).
 */
export function reverseQueue(): void {
  write(queueReverse(snapshot));
}

/**
 * One row up or down a place — the panel's per-row arrows. Not a request to play, for
 * {@link reverseQueue}'s reason: what is playing stays what it was. A move past either end is the same
 * state back (`queueMove` clamps), so the store writes nothing.
 */
export function moveInQueue(key: string, by: -1 | 1): void {
  const from = snapshot.entries.findIndex((entry) => entry.key === key);
  if (from !== -1) write(queueMove(snapshot, key, from + by));
}

export function playQueueEntry(key: string): void {
  write(queuePlay(snapshot, key), true);
}

export function advanceQueue(): void {
  write(queueAdvance(snapshot), true);
}

/**
 * Throw the list away — the watch panel's **Clear**, and the corner player's **Close**.
 *
 * Neither asks anything first, and that is the decision rather than an oversight: the corner player
 * is the queue's own window, so the press that puts the window away is the press that ends the list.
 * The alternative left a list parked with nothing on screen saying it was still there.
 */
export function clearQueue(): void {
  write(queueClear(snapshot), false);
}

/**
 * Let go of the playlist and keep the rows — the panel's Unlink, and the only act that takes a queue
 * from one face to the other without touching what is in it.
 *
 * The default `play` rather than `true`: the rows keep their places and the cursor keeps pointing at
 * the same one, so nothing about the playback is in question (see {@link playOnArrival}). Nothing is
 * fetched to complete the list first, which is `queueUnlink`'s own rule, and nothing is sent: the
 * playlist on the instance is untouched. The address has to lose its `list` in the same act, or the
 * panel's loader reads the playlist straight back in — which is why the caller is
 * `useWatchScreen`'s `listKeep.unlink` and not this function alone.
 */
export function unlinkQueue(): void {
  write(queueUnlink(snapshot));
}

/**
 * The queue becomes this window on that playlist — what a `list` in the address means
 * (`screens/watch/useWatchPlaylist.ts` is the one caller).
 *
 * Called again for every page that arrives, because it is a **projection** rather than an append: core
 * re-derives the rows from the run in hand each time, and their keys make the result the same rows the
 * reader was already looking at, only more of them.
 *
 * `window` is `null` while the playlist's header has not arrived. Either way the act has a second half:
 * a queue that is some **other** list's goes. The address names this playlist and the panel draws the
 * queue, so the tab's previous list left standing there would be a panel about a list the reader is not
 * watching. `queueFromPlaylist` declines a run that cannot place the cursor yet — the page holding the
 * playing entry is still on the wire — and that is the same state one page earlier, so both arms of the
 * decline are answered here.
 *
 * The `play` flag is left where it is: a projection is the app reading a playlist rather than anybody
 * pressing anything, and on a `list` route the video is the route's to start.
 */
export function projectPlaylistQueue(playlistId: string, window: QueuePlaylistWindow | null): void {
  const projected = window === null ? snapshot : queueFromPlaylist(snapshot, window);
  if (projected !== snapshot) {
    write(projected);
    return;
  }
  if (snapshot.link?.playlistId !== playlistId) write(queueClear(snapshot), false);
}

/**
 * Put the cursor on the video the address bar names, if the queue holds it.
 *
 * The one direction of the route↔queue relationship that is not a press: while the reader is on
 * `/watch`, the route is what is playing, so the queue follows it. Off `/watch` the cursor is the
 * authority and nothing calls this — see `playback/PlaybackHost.tsx`.
 */
export function followQueueVideo(videoId: string): void {
  write(queueFollowVideo(snapshot, videoId));
}
